/*
 * Copyright (c) 2023. Word Wide Generation Ltd
 */

import React from 'react';
import { UseQueryHookResult } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { QueryDefinition, ResultTypeFrom } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import Loader from '../loader';
import { BasicAlert } from '../alert/BasicAlert';


interface Props<D extends QueryDefinition<any, any, any, any>> {
  query: UseQueryHookResult<D>;
  onNoData?: () => React.JSX.Element;
  onSuccess: (data: ResultTypeFrom<D>) => React.JSX.Element | null;
  onError?: (error?: UseQueryHookResult<D>['error']) => React.JSX.Element | null;
  loader?: React.JSX.Element;
  enableLoader?: boolean;
}

export function QueryWrapper<D extends QueryDefinition<any, any, any, any>>(props: Props<D>): React.JSX.Element | null {
  const { query, onNoData, onSuccess, onError, enableLoader = true } = props;

  if (query.isUninitialized) {
    return props.loader ?? <Loader />;
  }
  if (enableLoader && (query.isLoading || query.isFetching)) {
    return props.loader ?? <Loader />;
  }

  if (query.isError) {
    if (onError) {
      return onError(query.error);
    }

    if ('message' in query.error) {
      return <BasicAlert type={'danger'}>{query.error.message}</BasicAlert>;
    }

    return <BasicAlert type={'danger'}>{query.error}</BasicAlert>;
  }

  if (!query.data || (Array.isArray(query.data) && query.data.length === 0)) {
    if (onNoData) {
      return onNoData();
    }
    return <div>No data available</div>;
  }

  return onSuccess(query.data);
}
